import { API_BASE, createFetchOptions } from './config'

export const thumbnailApi = {
  // Upload thumbnail directly through backend proxy
  async uploadThumbnail(
    modelId: string,
    file: Blob,
    size: '256' | '512' | '1024' | 'original' = '512',
    token?: string,
    onProgress?: (progress: number) => void
  ): Promise<string> {
    const url = `${API_BASE}/api/v2/admin/models/${modelId}/thumbnail/upload?size=${size}`
    
    if (onProgress) {
      // For progress tracking, use XMLHttpRequest
      const response = await new Promise<{ filePath: string }>((resolve, reject) => {
        const xhr = new XMLHttpRequest()
        
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const progress = (e.loaded / e.total) * 100
            onProgress(progress)
          }
        })

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const result = JSON.parse(xhr.responseText)
              if (result.success) {
                resolve(result.data)
              } else {
                reject(new Error(result.error || 'Upload failed'))
              }
            } catch (error) {
              reject(new Error('Invalid response'))
            }
          } else {
            try {
              const errorData = JSON.parse(xhr.responseText)
              reject(new Error(`Upload failed: ${errorData.error || xhr.statusText}${errorData.details ? ` - ${errorData.details}` : ''}`))
            } catch {
              reject(new Error(`Upload failed: ${xhr.statusText}`))
            }
          }
        })

        xhr.addEventListener('error', () => {
          reject(new Error('Upload failed'))
        })

        xhr.open('PUT', url)
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`)
        }
        xhr.send(file)
      })
      
      return response.filePath
    } else {
      const headers: HeadersInit = {}
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
      
      const response = await fetch(url, 
        createFetchOptions({
          method: 'PUT',
          headers,
          body: file,
        })
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(`Upload failed: ${errorData.error || response.statusText}${errorData.details ? ` - ${errorData.details}` : ''}`)
      }

      const result = await response.json()
      return result.data.filePath
    }
  },

  // Upload multiple thumbnail sizes
  async uploadThumbnailSizes(
    modelId: string,
    file: Blob,
    sizes: Array<'256' | '512' | '1024' | 'original'> = ['256', '512', '1024'],
    token?: string,
    onProgress?: (size: string, progress: number) => void
  ): Promise<Record<string, string>> {
    const results: Record<string, string> = {}
    
    for (const size of sizes) {
      try {
        const filePath = await this.uploadThumbnail(
          modelId,
          file,
          size,
          token,
          onProgress ? (progress) => onProgress(size, progress) : undefined
        )
        results[size] = filePath
      } catch (error) {
        console.error(`Failed to upload ${size} thumbnail:`, error)
        throw error
      }
    }
    
    return results
  }
}